import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import AuthService from 'services/auth';
import PublicNav from 'layouts/PublicNav';
import PublicFooter from 'layouts/PublicFooter';
import useScrollReveal from 'hooks/useScrollReveal';
import DemoShowcase from 'components/shared/GetStartedSteps';
import WorkflowUseCases from 'components/shared/WorkflowUseCases/WorkflowUseCases';
import MissionControlDemo from 'components/shared/MissionControlDemo';
import { PillButton } from 'components/atoms';
import { IonToast } from '@ionic/react';
import { warningOutline, closeCircle } from 'ionicons/icons';
import './ConnectPage.css';

const DOWNLOAD_BASE_URL = 'https://dl.zibby.app';
const DOWNLOAD_MANIFEST_URL = `${DOWNLOAD_BASE_URL}/download/latest/manifest.json`;

const DOWNLOAD_CANDIDATES = {
  macArm: [`${DOWNLOAD_BASE_URL}/download/latest/Zibby%20Studio-mac-arm64.dmg`],
  macIntel: [`${DOWNLOAD_BASE_URL}/download/latest/Zibby%20Studio-mac-x64.dmg`],
  winX64: [
    `${DOWNLOAD_BASE_URL}/download/latest/Zibby%20Studio-win-x64.exe`,
    `${DOWNLOAD_BASE_URL}/download/latest/Zibby%20Studio-win-x64.zip`,
  ],
  winArm: [
    `${DOWNLOAD_BASE_URL}/download/latest/Zibby%20Studio-win-arm64.exe`,
    `${DOWNLOAD_BASE_URL}/download/latest/Zibby%20Studio-win-arm64.zip`,
  ],
};

const DOWNLOAD_BUTTONS = [
  { key: 'macArm', label: 'macOS (Apple)' },
  { key: 'macIntel', label: 'macOS (Intel)' },
  { key: 'winX64', label: 'Windows (x64)' },
  { key: 'winArm', label: 'Windows (arm64)' },
];

const buildDownloadUrlFromKey = (key) => {
  if (!key) return '';
  const encodedPath = String(key)
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${DOWNLOAD_BASE_URL}/${encodedPath}`;
};

const pickPreferredArtifact = (artifacts, preferredExt = '.dmg') => {
  if (!Array.isArray(artifacts) || artifacts.length === 0) return null;
  const preferred = artifacts.find((artifact) =>
    String(artifact?.file || '')
      .toLowerCase()
      .endsWith(preferredExt)
  );
  return preferred || artifacts[0];
};

const resolveArtifactUrl = (artifact) => {
  if (!artifact) return '';
  if (artifact.versionedKey) return buildDownloadUrlFromKey(artifact.versionedKey);
  if (artifact.latestKey) return buildDownloadUrlFromKey(artifact.latestKey);
  if (artifact.file) return `${DOWNLOAD_BASE_URL}/download/latest/${encodeURIComponent(artifact.file)}`;
  return '';
};

function detectPreferredVariant() {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const uaData = navigator.userAgentData;
  const arch = uaData?.architecture || '';
  const hasArmSignal = /arm|aarch64/i.test(arch) || /arm|aarch64/i.test(ua);
  const hasIntelSignal = /x86|x64|amd64|intel/i.test(arch) || /x86_64|x64|intel/i.test(ua);
  const isMac = /mac/i.test(platform) || /mac os/i.test(ua);
  const isWin = /win/i.test(platform) || /windows/i.test(ua);

  // Browser architecture reporting is often limited on macOS (especially Safari),
  // so default to Apple Silicon unless Intel is explicitly reported.
  if (isMac) {
    if (hasArmSignal) return 'macArm';
    if (hasIntelSignal) return 'macIntel';
    return 'macArm';
  }
  if (isWin) return hasArmSignal ? 'winArm' : 'winX64';
  return '';
}

// Demo stage background — locked to 'painted'. The other variants
// ('stripes' [orig], 'aurora', 'topo', 'grain', 'mesh') are still
// defined in ConnectPage.css if we want to swap later; just change
// this constant.
const DEMO_BG_VARIANT = 'painted';

const ConnectPage = () => {
  const { t } = useTranslation();
  const [toast, setToast] = useState({ isOpen: false, message: '', color: 'danger', icon: null });
  const showToast = (message, type = 'error') => {
    setToast({
      isOpen: true,
      message,
      color: type === 'warning' ? 'warning' : 'danger',
      icon: type === 'warning' ? warningOutline : closeCircle
    });
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageRef = useScrollReveal();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [error, setError] = useState(null);
  const [passcodeStep, setPasscodeStep] = useState('email');
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [passcodeLoading, setPasscodeLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const verifyingRef = useRef(false);
  const [desktopDownloads, setDesktopDownloads] = useState({
    macArm: DOWNLOAD_CANDIDATES.macArm[0],
    macIntel: DOWNLOAD_CANDIDATES.macIntel[0],
    winX64: DOWNLOAD_CANDIDATES.winX64[0],
    winArm: DOWNLOAD_CANDIDATES.winArm[0],
    preferred: detectPreferredVariant(),
  });

  const visibleDownloadKeys = (() => {
    const preferred = String(desktopDownloads.preferred || '');
    if (preferred.startsWith('mac')) return ['macArm', 'macIntel'];
    if (preferred.startsWith('win')) return ['winX64', 'winArm'];
    return DOWNLOAD_BUTTONS.map((button) => button.key);
  })();

  // CLI device flow MUST land on the studio subdomain (where the user
  // actually has a session). If a stale CLI sends them to root
  // zibby.dev/connect?deviceCode=..., bounce to studio with the same
  // query string so login + post-login redirect both happen in the
  // correct cookie scope. See CliAuthorizePage for the parallel
  // redirect — together they make root zibby.dev a no-op for CLI.
  useEffect(() => {
    const deviceCodeFromUrl = searchParams.get('deviceCode');
    if (!deviceCodeFromUrl) return;
    const host = window.location.hostname;
    const onRootZibby =
      host === 'zibby.dev' || host === 'zibby.app' ||
      host === 'www.zibby.dev' || host === 'www.zibby.app';
    if (onRootZibby) {
      const studioHost = host.startsWith('www.') ? host.replace('www.', 'studio.') : `studio.${host}`;
      window.location.replace(`https://${studioHost}/connect${window.location.search}`);
    }
  }, [searchParams]);

  // Persist returnTo, plan, and deviceCode from URL params so they survive the OAuth redirect
  useEffect(() => {
    const returnTo = searchParams.get('returnTo');
    const plan = searchParams.get('plan');
    const deviceCode = searchParams.get('deviceCode');

    if (returnTo) {
      sessionStorage.setItem('oauth_return_url', plan ? `${returnTo}?plan=${plan}` : returnTo);
    }
    if (plan) {
      sessionStorage.setItem('selected_plan', plan);
    }
    if (deviceCode) {
      sessionStorage.setItem('cli_device_code', deviceCode);
    }
  }, [searchParams]);

  // Check if user is already authenticated
  useEffect(() => {
    if (AuthService.isAuthenticated()) {
      // CLI authorization: check URL param first, then sessionStorage (survives OAuth redirect)
      const deviceCode = searchParams.get('deviceCode') || sessionStorage.getItem('cli_device_code');
      if (deviceCode) {
        sessionStorage.removeItem('cli_device_code');
        navigate(`/authorize?deviceCode=${deviceCode}`);
        return;
      }
      
      // If there's a returnTo or plan param, redirect there
      const returnTo = searchParams.get('returnTo');
      const plan = searchParams.get('plan');
      
      if (returnTo || plan) {
        if (returnTo) {
          navigate(plan ? `${returnTo}?plan=${plan}` : returnTo);
        } else {
          navigate('/projects');
        }
      }
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    setDesktopDownloads((prev) => ({ ...prev, preferred: detectPreferredVariant() }));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const hydrateDownloadsFromManifest = async () => {
      try {
        const response = await fetch(DOWNLOAD_MANIFEST_URL, { cache: 'no-store' });
        if (!response.ok) return;
        const payload = await response.json();
        const artifacts = Array.isArray(payload?.artifacts) ? payload.artifacts : [];
        const fileArtifacts = artifacts
          .filter((artifact) => artifact && typeof artifact === 'object')
          .filter((artifact) => {
            const file = String(artifact.file || '');
            return file && !file.endsWith('.blockmap');
          });
        if (fileArtifacts.length === 0) return;

        const macArmArtifact = pickPreferredArtifact(
          fileArtifacts.filter((artifact) => /mac-arm64\.(dmg|zip)$/i.test(String(artifact.file || ''))),
          '.dmg'
        );
        const macIntelArtifact = pickPreferredArtifact(
          fileArtifacts.filter((artifact) => /mac-(x64|intel)\.(dmg|zip)$/i.test(String(artifact.file || ''))),
          '.dmg'
        );
        const winX64Artifact = pickPreferredArtifact(
          fileArtifacts.filter((artifact) => /win[-_]x64\.(exe|zip|msi)$/i.test(String(artifact.file || ''))),
          '.exe'
        );
        const winArmArtifact = pickPreferredArtifact(
          fileArtifacts.filter((artifact) => /win[-_]arm64\.(exe|zip|msi)$/i.test(String(artifact.file || ''))),
          '.exe'
        );

        if (cancelled) return;
        setDesktopDownloads((prev) => ({
          ...prev,
          macArm: resolveArtifactUrl(macArmArtifact) || prev.macArm,
          macIntel: resolveArtifactUrl(macIntelArtifact) || prev.macIntel,
          winX64: resolveArtifactUrl(winX64Artifact) || prev.winX64,
          winArm: resolveArtifactUrl(winArmArtifact) || prev.winArm,
        }));
      } catch {
        // Keep static fallback URLs if manifest is temporarily unavailable.
      }
    };

    hydrateDownloadsFromManifest();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError(null);
    
    try {
      // This will redirect to Google OAuth, no return expected
      await AuthService.googleSignIn();
      // Note: This line won't execute due to redirect
    } catch (err) {
      setError('Failed to sign in with Google. Please try again.');
      console.error('Google sign in error:', err);
      setGoogleLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setMicrosoftLoading(true);
    setError(null);
    
    try {
      // This will redirect to Microsoft OAuth, no return expected
      await AuthService.microsoftSignIn();
      // Note: This line won't execute due to redirect
    } catch (err) {
      setError('Failed to sign in with Microsoft. Please try again.');
      console.error('Microsoft sign in error:', err);
      setMicrosoftLoading(false);
    }
  };

  const handleSendPasscode = async (e) => {
    if (e) e.preventDefault();
    const trimmed = (email || '').trim().toLowerCase();
    setError(null);
    setPasscodeLoading(true);
    try {
      await AuthService.requestPasscode(trimmed);
      setEmail(trimmed);
      setPasscodeStep('verify');
      setPasscode('');
    } catch (err) {
      if (err.retryAfter) {
        showToast(`Please wait ${err.retryAfter}s before requesting a new code`, 'warning');
      } else {
        setError(err.message || 'Failed to send passcode. Please try again.');
      }
    } finally {
      setPasscodeLoading(false);
    }
  };

  const handleVerifyPasscode = async () => {
    if (verifyingRef.current) return;
    const code = (passcode || '').trim();
    if (!code || code.length !== 6) {
      setError('Please enter the 6-digit passcode from your email.');
      return;
    }
    verifyingRef.current = true;
    setError(null);
    setVerifyLoading(true);
    try {
      await AuthService.verifyPasscode(email, code);

      const cliDeviceCode = sessionStorage.getItem('cli_device_code');
      if (cliDeviceCode) {
        sessionStorage.removeItem('cli_device_code');
        navigate(`/authorize?deviceCode=${cliDeviceCode}`);
        return;
      }

      const returnUrl = sessionStorage.getItem('oauth_return_url');
      sessionStorage.removeItem('oauth_return_url');
      sessionStorage.removeItem('selected_plan');
      navigate(returnUrl || '/projects');
    } catch (err) {
      showToast(err.message || 'Invalid or expired passcode. Please try again.', 'error');
    } finally {
      setVerifyLoading(false);
      verifyingRef.current = false;
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign In - Zibby</title>
        <meta name="description" content="Sign in to Zibby to access your AI productivity assistant." />
      </Helmet>
      <div className="login-wrapper" ref={pageRef}>
        <div className="login-page fade-in">
          <PublicNav transparent />
        <div className="login-split-layout reveal">
          <div className="login-split-left">
          <div className="login-container">
          <div className="login-card">
            {/* Header */}
            <div className="login-header">
            <h1>{t('connect.title')}</h1>
            <p className="login-tagline">The cloud pipeline for Claude Code, Cursor, Codex, and Gemini</p>
            <p className="login-subtitle">
              {t('connect.subtitle')}
            </p>
          </div>

          {/* Authentication Options */}
          <div>
            <div className="login-buttons">
              {/* Google Sign In */}
              <button
                className="login-button google-button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading || microsoftLoading}
              >
                <>
                  {googleLoading ? (
                    <div className="spinner"></div>
                  ) : (
                    <svg className="button-icon google-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  )}
                  <span className="button-text">{t('connect.connectWithGmail')}</span>
                </>
              </button>

              {/* Microsoft Sign In */}
              <button
                className="login-button microsoft-button"
                onClick={handleMicrosoftSignIn}
                disabled={googleLoading || microsoftLoading}
              >
                <>
                  {microsoftLoading ? (
                    <div className="spinner"></div>
                  ) : (
                    <svg className="button-icon" viewBox="0 0 24 24" fill="none">
                      <path d="M11.4 11.4H0V0h11.4v11.4z" fill="#f25022"/>
                      <path d="M24 11.4H12.6V0H24v11.4z" fill="#00a4ef"/>
                      <path d="M11.4 24H0V12.6h11.4V24z" fill="#ffb900"/>
                      <path d="M24 24H12.6V12.6H24V24z" fill="#7fba00"/>
                    </svg>
                  )}
                  <span className="button-text">{t('connect.connectWithOutlook')}</span>
                </>
              </button>
            </div>
            <div className="login-divider">
              <span className="login-divider-line" />
              <span className="login-divider-text">{t('connect.orDivider')}</span>
              <span className="login-divider-line" />
            </div>

            {passcodeStep === 'email' ? (
              <form className="login-email-section" onSubmit={handleSendPasscode} noValidate={false}>
                <input
                  type="email"
                  required
                  className="login-input"
                  placeholder={t('connect.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={passcodeLoading}
                  autoComplete="email"
                />
                <PillButton
                  type="submit"
                  className="login-button login-button-email"
                  disabled={passcodeLoading}
                >
                  {passcodeLoading ? <div className="spinner" /> : null}
                  <span className="button-text">{t('connect.sendPasscode')}</span>
                </PillButton>
              </form>
            ) : (
              <div className="login-email-section">
                <p className="login-check-email">{t('connect.checkEmail')}</p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className="login-input login-input-passcode"
                  placeholder={t('connect.passcodePlaceholder')}
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value.replace(/\D/g, ''))}
                  disabled={verifyLoading}
                  autoComplete="one-time-code"
                />
                <PillButton
                  type="button"
                  className="login-button login-button-email"
                  onClick={handleVerifyPasscode}
                  disabled={verifyLoading || passcode.length !== 6}
                >
                  {verifyLoading ? <div className="spinner" /> : null}
                  <span className="button-text">{t('connect.verifyPasscode')}</span>
                </PillButton>
                <div className="login-resend-row">
                <button
                  type="button"
                  className="login-resend-link"
                  onClick={async () => {
                    setError(null);
                    setPasscodeLoading(true);
                    try {
                      await AuthService.requestPasscode(email);
                      setError(null);
                    } catch (err) {
                      if (err.retryAfter) {
                        showToast(`Please wait ${err.retryAfter}s before requesting a new code`, 'warning');
                      } else {
                        setError(err.message || 'Failed to resend.');
                      }
                    } finally {
                      setPasscodeLoading(false);
                    }
                  }}
                  disabled={passcodeLoading}
                >
                  {t('connect.resendPasscode')}
                </button>
                <span className="login-resend-sep">·</span>
                <button
                  type="button"
                  className="login-resend-link"
                  onClick={() => { setPasscodeStep('email'); setEmail(''); setPasscode(''); setError(null); }}
                >
                  {t('connect.useDifferentEmail')}
                </button>
              </div>
              </div>
            )}

            {/* Error Message - below buttons */}
            <div className="login-error" aria-live="polite">
              {error && (
                <div className="error-content">
                  <span className="error-text">{error}</span>
                  <button
                    className="error-close"
                    onClick={() => setError(null)}
                    type="button"
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            {/* <button
              className="login-button facebook-button"
              onClick={handleFacebookSignIn}
              disabled={googleLoading || microsoftLoading || facebookLoading}
            >
              <>
                {facebookLoading ? (
                  <div className="spinner"></div>
                ) : (
                  <svg className="button-icon facebook-icon" viewBox="0 0 24 24" fill="none">
                    <rect x="0" y="0" width="24" height="24" rx="3" ry="3" fill="#1877F2"/>
                    <path d="M15.12 12.61l.4-2.61h-2.5V8.5c0-.71.35-1.41 1.47-1.41h1.14V4.88s-1.03-.18-2.02-.18c-2.06 0-3.41 1.25-3.41 3.51V10H7.85v2.61h2.35V20h2.89v-7.39h2.03z" fill="white"/>
                  </svg>
                )}
                <span className="button-text">Continue with Facebook</span>
              </>
            </button> */}
          </div>
          {/* Privacy Notice */}
          <div className="login-footer">
            <p>
              {t('connect.termsText')} <a href="/terms" target="_blank" rel="noopener noreferrer">{t('connect.termsOfService')}</a> {t('connect.and')} <a href="/privacy" target="_blank" rel="noopener noreferrer">{t('connect.privacyPolicy')}</a>. 
              {t('connect.linkDescription')}
            </p>
          </div>
          </div>
        </div>
          </div>
          <div className="login-split-right">
            <DemoShowcase />
          </div>
        </div>

        <WorkflowUseCases />

        <section className="desktop-download-section reveal">
          <div className="desktop-download-grid">
            <div className="desktop-download-copy">
              <h2 className="desktop-download-title">Studio — desktop UI for test workflow runs</h2>
              <p className="desktop-download-subtitle">
                Visualise live test runs, scrub past sessions, stop a workflow with a click. Test recipe today; more workflows soon.
              </p>
              <div className="desktop-download-actions">
                {visibleDownloadKeys.length === 2 &&
                visibleDownloadKeys.includes('macArm') &&
                visibleDownloadKeys.includes('macIntel') ? (
                  <div className="desktop-download-mac-stack">
                    <PillButton
                      className="desktop-download-btn desktop-download-btn--apple"
                      emphasized
                      href={desktopDownloads.macArm || undefined}
                      target={desktopDownloads.macArm ? '_blank' : undefined}
                      rel={desktopDownloads.macArm ? 'noopener noreferrer' : undefined}
                      disabled={!desktopDownloads.macArm}
                    >
                      Download macOS (Apple)
                    </PillButton>
                    <a
                      className={`desktop-download-intel-link ${
                        desktopDownloads.macIntel ? '' : 'desktop-download-intel-link--disabled'
                      }`}
                      href={desktopDownloads.macIntel || undefined}
                      target={desktopDownloads.macIntel ? '_blank' : undefined}
                      rel={desktopDownloads.macIntel ? 'noopener noreferrer' : undefined}
                      aria-disabled={desktopDownloads.macIntel ? 'false' : 'true'}
                    >
                      Download for Intel Mac
                    </a>
                  </div>
                ) : (
                  DOWNLOAD_BUTTONS.filter((button) => visibleDownloadKeys.includes(button.key)).map((button) => {
                    const href = desktopDownloads[button.key];
                    const isRecommended = desktopDownloads.preferred === button.key;
                    return (
                      <PillButton
                        key={button.key}
                        className={`desktop-download-btn ${
                          href ? '' : 'desktop-download-btn--disabled'
                        }`}
                        emphasized={isRecommended}
                        href={href || undefined}
                        target={href ? '_blank' : undefined}
                        rel={href ? 'noopener noreferrer' : undefined}
                        disabled={!href}
                      >
                        {button.label}
                      </PillButton>
                    );
                  })
                )}
              </div>
            </div>
            <div className="desktop-download-preview">
              <div
                className={
                  DEMO_BG_VARIANT === 'stripes'
                    ? 'desktop-demo-stage'
                    : `desktop-demo-stage desktop-demo-stage--bg-${DEMO_BG_VARIANT}`
                }
              >
                <div className="desktop-browser-shell">
                  <div className="desktop-browser-shell__header">
                    <div className="desktop-browser-shell__traffic">
                      <span />
                      <span />
                      <span />
                    </div>
                    <span className="desktop-browser-shell__title">Mission Control</span>
                  </div>
                  <div className="desktop-browser-shell__body">
                    <MissionControlDemo />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        </div>
        <PublicFooter />
      </div>
      <IonToast
        isOpen={toast.isOpen}
        onDidDismiss={() => setToast(prev => ({ ...prev, isOpen: false }))}
        message={toast.message}
        duration={4000}
        icon={toast.icon}
        position="bottom"
        cssClass="custom-toast"
      />
    </>
  );
};

export default ConnectPage;
